import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DOCUMENT} from "@angular/common";
import {Subscription, timer} from "rxjs";
import {delay, finalize} from "rxjs/operators";

import {api_url, api_url_replace, BBS_CATEGORY} from '../shared/defines/protocol';
import {
  BBS_INFO, DEFAULT_SCROLL_SPEED, boardSelectId, CONTAINER_SCROLL_DELAY, DETAIL_POPUP_DELAY, HISTORY_KEY_SEARCH_BOARD,
  LINK_TYPE,
  LinkInfo2,
  LIST_TYPE, SearchParam,
  triggerCollapse, BOARD_SCROLL_OFFSETY,
} from '../shared/defines/client.const';
import {IsEmptyVar, IsEqualObj1} from '../shared/defines/common.func';
import {scrollToNode} from '../shared/defines/client.func';
import {NormalizeSearchParamKeyword} from '../shared/defines/search-keyword.func';
import {Article} from '../shared/models/article.model';
import {LayoutMgrService} from '../shared/services/layout-mgr/layout-mgr.service';
import {BoardService} from '../shared/services/board/board.service';
import {GlobalEventService} from "../shared/services/global-event/global-event.service";
import {ScrollDownService} from "../shared/services/scroll-down/scroll-down.service";
import {SeoService} from '../shared/services/seo/seo.service';

const SEO_DESCRIPTION_MAX_LENGTH = 160;

@Component({
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss'],
  animations: [ triggerCollapse ],
  encapsulation: ViewEncapsulation.None,
})
export class BoardComponent implements OnInit, OnDestroy {
  @ViewChild('container') container: ElementRef;

  ApiUrl = api_url;
  ApiUrlReplace = api_url_replace;
  boardSelectId = boardSelectId;

  HistoryKeySearch = HISTORY_KEY_SEARCH_BOARD;

  private category: BBS_CATEGORY; // 현재 파라미터로 받아온 게시판 범주(게시판 구분자)
  private routeId: string | null = null; // 경로 파라미터에서 받아온 게시글 번호

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam = null;
  private queryParams$: Subscription = null;
  private routeParams$: Subscription = null;

  // 푸터의 아래로 내리기 버튼 구독
  private scrollDown$: Subscription | null = null;

  public get forceScroll(): boolean {
    return this.routeId !== null && this.selectNo !== null;
  }


  public listArticle: Array<Article> = null;
  public selectNo: number = null; // 현재 선택된 게시글 번호
  protected selectedArticle: Article | null = null;
  private article$: Subscription | null = null;

  public placeholder: string;
  public keyword: string;


  constructor(
    @Inject(DOCUMENT) private document: Document,
    private route: ActivatedRoute,
    private router: Router,
    private board: BoardService,
    private globalEventSvc: GlobalEventService,
    private scrollDownSvc: ScrollDownService,
    private seoSvc: SeoService,
    public lm: LayoutMgrService,
  ) {
    /******************************************************************************************
     * 쿼리 파라미터 변경 핸들러 - 주소 뒤의 파라미터를 받아오는 곳. ( ?keyword=... )
     * id는 경로 파라미터로 이동했으므로 여기서는 keyword 등만 처리한다.
     */
    this.queryParams$ = this.route.queryParams.subscribe(params => {
      console.log('queryParams =', params);
      const oldParams = this.queryParams;
      this.queryParams = NormalizeSearchParamKeyword({...params});

      if (!IsEmptyVar(this.category) && !IsEqualObj1(oldParams, this.queryParams)) {
        this.init();
      }
    });

    /******************************************************************************************
     * 경로 파라미터 변경 핸들러 - cat과 id를 받아오는 곳.
     * /board/:cat 또는 /board/:cat/:id 경로에서 파라미터를 읽는다.
     *
     * 같은 라우트 설정 내에서 파라미터만 변경되면 컴포넌트가 재생성되지 않고
     * 이 핸들러가 다시 호출된다. (예: /board/2/6 → /board/2/7)
     */
    this.routeParams$ = route.params.subscribe(params => {
      const newCat = params.cat;
      const newId: string | undefined = params.id;

      if (this.category !== newCat) {
        this.category = newCat;
        this.routeId = newId ?? null;
        console.log('board/' + this.category);

        this.init();
      } else if ((newId ?? null) !== this.routeId) {
        // 같은 게시판에서 id만 변경 (예: /board/2/6 → /board/2/7)
        this.routeId = newId ?? null;

        if (this.routeId) {
          this.processArticleId();
        } else {
          this.Fold();
        }
      }
    });
  }

  ngOnInit(): void {
    // 푸터의 스크롤 버튼 처리.
    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }

  ngOnDestroy(): void {
    if (this.queryParams$) {
      this.queryParams$.unsubscribe();
      this.queryParams$ = null;
    }
    if (this.routeParams$) {
      this.routeParams$.unsubscribe();
      this.routeParams$ = null;
    }

    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }

    this.article$?.unsubscribe();
    this.article$ = null;
  }

  /****************************************************************************
   * 게시판 목록 로딩 처리.
   ****************************************************************************/

  private init() : void {
    this.placeholder = `${BBS_INFO[this.category].placeholder} 내에서 검색 합니다.`;
    this.keyword = 'keyword' in this.queryParams ? this.queryParams.keyword : null;
    this.applyBoardListSeo();

    this.ReloadList(this.queryParams);
  }

  public onSearch(param: SearchParam): void {
    console.log('onSearch() param =', param);
    this.router.navigate(['/board', this.category], {
      queryParams: {...NormalizeSearchParamKeyword(param), bbs_cat: this.category},
    }).then();
  }

  public ReloadList(param: SearchParam): void {
    console.log('BoardComponent::ReloadList() = ', param);

    this.lm.Reset();

    // tslint:disable-next-line:variable-name
    const _param: SearchParam = {
      ...NormalizeSearchParamKeyword(this.queryParams),
      bbs_cat: this.category,
    };
    this.board.GetList(_param).subscribe(
      (data: Array<Article>) => {
        console.log('BoardComponent::GetList() data =', data);
        this.listArticle = data.map(value => new Article(value));

        // console.warn('BoardComponent::GetList() listArticle =', data, this.listArticle);
        this.scrollToArticleIf();
      },
      err => console.error('BoardComponent::GetList() error =', err)
    );
  }

  /**
   * 해당 게시글에 scroll 해야하는지 확인하고 필요하면 스크롤 한다.
   * @private
   */
  private scrollToArticleIf(): void {
    if (this.routeId) {
      const idx = parseInt(this.routeId, 10);
      const article = this.listArticle.find(value => value.no === idx);
      // 해당 게시글이 존재하면 이동해서 게시글을 연다.
      if (IsEmptyVar(article)) {
        console.error('BoardComponent::scrollToArticleIf() article does not exist', idx);
      }
      else {
        console.log('Find article=', article);

        this.Unfoild(article);

        // 아직 로딩된 데이터가 화면에 그려지지 않았기 때문에 대기한 뒤에 스크롤한다.
        timer(CONTAINER_SCROLL_DELAY).subscribe(
          () => this.scrollToArticle(article)
        );
      }
    }
  }

  /**
   * 같은 게시판 내에서 id가 변경되었을 때 해당 게시글을 열고 스크롤한다.
   */
  private processArticleId(): void {
    const processParams = () => {
      const article = this.listArticle.find(value => value.no === parseInt(this.routeId, 10));

      if (article) {
        timer(DETAIL_POPUP_DELAY).subscribe(() => {
          this.Unfoild(article);
          timer(CONTAINER_SCROLL_DELAY).subscribe(
            () => this.scrollToArticle(article)
          );
        });
      } else {
        console.error('BoardComponent::processArticleId() article does not exist', this.routeId);
        this.init();
      }
    };

    if (this.board.IsLoading) {
      this.board.GetList().pipe(
        delay(DETAIL_POPUP_DELAY),
      ).subscribe(processParams);
    }
    else {
      processParams();
    }
  }

  private scrollToArticle(article: Article): void {
    const el = this.document.getElementById(boardSelectId(article));
    if (el) {
      const elContainer = this.container.nativeElement;
      scrollToNode(elContainer, el, - BOARD_SCROLL_OFFSETY );
    }
    else {
      console.error('BoardComponent::scrollToArticleIf() article element does not exist', article);
    }
  }

  /****************************************************************************
   * 게시글 선택 처리 코드
   ***************************************************************************/

  public onSelect($event: MouseEvent, article: Article): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    this.router.navigate(['/board', this.category, article.no]).then();
  }

  onClose($event: MouseEvent) {
    // console.warn("BoardComponent:onClose()", article);
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    this.router.navigate(['/board', this.category]).then();
  }

  private Unfoild(article: Article): void {
    // console.warn("BoardComponent::Unfould()", article);
    // this.selectNo = this.selectNo === article.no ? null : article.no;
    this.selectNo = article.no;

    this.article$ = this.board.GetDetail(article.no).pipe(
      finalize(() => this.article$ = null),
    ).subscribe(
      article => {
        this.selectedArticle = new Article(article);
        this.applyArticleSeo(this.selectedArticle);
      },
      err => {
        console.error('BoardComponent::GetDetail() error =', err);
        // 상세 로딩 실패 시 상세 메타가 멈추지 않도록 게시판 목록 SEO로 복원한다.
        this.applyBoardListSeo();
      }
    );

    // if ('id' in this.queryParams) {
    //   delete this.queryParams.id; // 더 이상 자동 열리기가 작동하지 않도록 한다.
    // }
  }

  private Fold(): void {
    this.selectNo = null;
    this.selectedArticle = null;
    this.applyBoardListSeo();
  }

  private applyBoardListSeo(): void {
    this.seoSvc.applyRouteSeo(this.router.url);
  }

  private applyArticleSeo(article: Article): void {
    const pageName = this.toSeoPlainText(article.title) || this.getBoardPageName();
    const description = this.buildArticleSeoDescription(article);

    this.seoSvc.applyDynamicSeo(this.router.url, {
      pageName,
      description,
    });
  }

  private buildArticleSeoDescription(article: Article): string {
    const description = this.toSeoPlainText(article.brief)
      || this.toSeoPlainText(article.content)
      || this.toSeoPlainText(article.title)
      || `${this.getBoardPageName()} 게시판의 글입니다.`;

    return this.limitSeoText(description, SEO_DESCRIPTION_MAX_LENGTH);
  }

  private toSeoPlainText(value: string | null | undefined): string {
    if (IsEmptyVar(value)) {
      return '';
    }

    const el = this.document.createElement('div');
    el.innerHTML = value;
    el.querySelectorAll('script, style, template').forEach(node => node.remove());

    return (el.textContent ?? '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private limitSeoText(value: string, maxLength: number): string {
    return value.length > maxLength ? value.slice(0, maxLength).trim() : value;
  }

  private getBoardPageName(): string {
    return BBS_INFO[this.category]?.placeholder ?? '게시판';
  }

  /****************************************************************************
   * 링크 팝업 처리 코드
   ***************************************************************************/
  public gotoLink(linkInfo: LinkInfo2): void {

    switch (linkInfo.type) {
      case LINK_TYPE.WORD:
      {
        console.log('gotoLink()', linkInfo);
        const queryParams: SearchParam = {
          id: linkInfo.id,
          l: LIST_TYPE.HISTORY,
        };
        this.router.navigate(['/word'], {queryParams}).then();
        break;
      }
      case LINK_TYPE.DIALECT:
      {
        console.log('gotoLink()', linkInfo);
        const queryParams: SearchParam = {
          id: linkInfo.id,
          l: LIST_TYPE.HISTORY,
        };
        this.router.navigate(['/dialect'], {queryParams}).then();
        break;
      }
      default:
        console.error('MovieCollectComponent::gotoLink() invalid linkInfo', linkInfo);
        break;
    }
  }

}
